import { Link } from "react-router-dom";
import { Sunrise, Phone, Mail, MapPin, Clock } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { BUSINESS } from "../data/content";

export const Footer = () => {
  const { lang, t } = useLanguage();
  const name = lang === "hi" ? BUSINESS.nameHi : BUSINESS.nameEn;
  const address = lang === "hi" ? BUSINESS.addressHi : BUSINESS.addressEn;

  return (
    <footer className="relative overflow-hidden bg-maroon-dark text-ivory" data-testid="footer">
      <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full border border-gold/20" />
      <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full border border-gold/10" />

      <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 sm:px-8 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-gold text-maroon-dark">
              <Sunrise size={22} strokeWidth={1.8} />
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.4em] text-gold">HB Group</span>
          </div>
          <h3 className={`mt-6 text-4xl leading-tight sm:text-5xl ${lang === "hi" ? "font-hindi" : "font-display"}`} data-testid="footer-business-name">
            {name}
          </h3>
          <p className={`mt-3 text-sm tracking-wide text-ivory/60 ${lang === "hi" ? "font-hindi" : ""}`}>
            {lang === "hi" ? BUSINESS.tagHi : BUSINESS.tagEn}
          </p>

          <div className="mt-10 space-y-5 text-sm">
            <div className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 shrink-0 text-gold" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80">{t.footer.visit}</p>
                <p className={`mt-1 text-ivory/85 ${lang === "hi" ? "font-hindi" : ""}`} data-testid="footer-address">{address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone size={16} className="mt-0.5 shrink-0 text-gold" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80">{t.footer.call}</p>
                <p className="mt-1 text-ivory/85" data-testid="footer-phones">
                  {BUSINESS.phones.map((p, i) => (
                    <span key={p}>
                      <a href={`tel:${p}`} className="transition-colors duration-300 hover:text-gold" data-testid={`footer-phone-${i}`}>{p}</a>
                      {i < BUSINESS.phones.length - 1 && <span className="mx-1.5 text-ivory/30">·</span>}
                    </span>
                  ))}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={16} className="mt-0.5 shrink-0 text-gold" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80">{t.footer.write}</p>
                <a href={`mailto:${BUSINESS.email}`} className="mt-1 block text-ivory/85 transition-colors duration-300 hover:text-gold" data-testid="footer-email">
                  {BUSINESS.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={16} className="mt-0.5 shrink-0 text-gold" />
              <p className={`mt-0.5 text-ivory/85 ${lang === "hi" ? "font-hindi" : ""}`} data-testid="footer-hours">{t.footer.hours}</p>
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80">{t.footer.links}</p>
          <ul className="mt-5 space-y-3 text-sm">
            <li><Link to="/" data-testid="footer-nav-home" className={`text-ivory/70 transition-colors duration-300 hover:text-gold ${lang === "hi" ? "font-hindi" : ""}`}>{t.nav.home}</Link></li>
            <li><Link to="/about" data-testid="footer-nav-about" className={`text-ivory/70 transition-colors duration-300 hover:text-gold ${lang === "hi" ? "font-hindi" : ""}`}>{t.nav.about}</Link></li>
            <li><Link to="/fabrics" data-testid="footer-nav-fabrics" className={`text-ivory/70 transition-colors duration-300 hover:text-gold ${lang === "hi" ? "font-hindi" : ""}`}>{t.nav.fabrics}</Link></li>
            <li><Link to="/contact" data-testid="footer-nav-contact" className={`text-ivory/70 transition-colors duration-300 hover:text-gold ${lang === "hi" ? "font-hindi" : ""}`}>{t.nav.contact}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80">{t.contact.mapLabel}</p>
          <div className="img-print mt-5 overflow-hidden rounded-xl border border-gold/25">
            <iframe
              title="HB Bhavan Map"
              src={BUSINESS.mapSrc}
              className="h-64 w-full grayscale-[35%] transition-all duration-700 hover:grayscale-0"
              loading="lazy"
              data-testid="footer-map"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gold/15">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-2 px-5 py-6 text-xs text-ivory/50 sm:flex-row sm:items-center sm:px-8">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} {name} · HB Group. {t.footer.rights}</p>
          <p className="font-hindi text-gold/70">सूतापट्टी · मुज़फ़्फ़रपुर · बिहार</p>
        </div>
      </div>
    </footer>
  );
};
